<script setup>
import { ref, computed } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import ApiResponseTree from './ApiResponseTree.vue';
import SlotFieldsPicker from './SlotFieldsPicker.vue';
import ContactFieldsPicker from './ContactFieldsPicker.vue';
import SlotValuesPicker from './SlotValuesPicker.vue';

const props = defineProps({
  agentBotId: { type: Number, default: null },
  excludeSlotId: { type: [Number, String], default: null },
  allSlots: { type: Array, default: () => [] },
  apiSample: { type: [Object, Array, String, Number, Boolean], default: null },
  hideApiTab: { type: Boolean, default: false },
  label: { type: String, default: null },
});

const emit = defineEmits(['pick']);

const open = ref(false);
const tab = ref('field');

const availableTabs = computed(() => {
  const tabs = [
    { id: 'field', label: 'Fields', icon: 'i-lucide-tag' },
    { id: 'contact', label: 'Contato', icon: 'i-lucide-user' },
    { id: 'slot', label: 'Slots', icon: 'i-lucide-layers' },
  ];
  if (!props.hideApiTab) {
    tabs.push({ id: 'api', label: 'Resposta API', icon: 'i-lucide-globe' });
  }
  return tabs;
});

const togglePanel = () => {
  open.value = !open.value;
};

const close = () => {
  open.value = false;
};

const onPick = payload => {
  emit('pick', payload);
};
</script>

<template>
  <div
    v-on-click-outside="close"
    class="relative inline-block"
  >
    <button
      type="button"
      class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-woot-300 dark:border-woot-700 text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/20 hover:bg-woot-100 dark:hover:bg-woot-900/30"
      @click="togglePanel"
    >
      <span class="i-lucide-braces w-3 h-3" />
      {{ label || $t('OMNI.SLOTS.API_AUTOMATIONS.INSERT_VARIABLE', 'Inserir variável') }}
      <span :class="open ? 'i-lucide-chevron-up w-3 h-3' : 'i-lucide-chevron-down w-3 h-3'" />
    </button>

    <div
      v-if="open"
      class="absolute right-0 z-40 mt-1 w-80 max-w-[min(20rem,calc(100vw-2rem))] max-h-[60vh] overflow-auto rounded-md shadow-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-2"
    >
      <div class="flex border-b border-slate-200 dark:border-slate-700 mb-2">
        <button
          v-for="t in availableTabs"
          :key="t.id"
          type="button"
          class="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-medium border-b-2"
          :class="tab === t.id
            ? 'border-woot-500 text-woot-600 dark:text-woot-400'
            : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'"
          @click="tab = t.id"
        >
          <span :class="`${t.icon} w-3 h-3`" />
          {{ t.label }}
        </button>
      </div>

      <div class="min-h-[120px]">
        <SlotFieldsPicker
          v-if="tab === 'field'"
          :agent-bot-id="agentBotId"
          :exclude-slot-id="excludeSlotId"
          @pick="onPick"
        />
        <ContactFieldsPicker
          v-else-if="tab === 'contact'"
          @pick="onPick"
        />
        <SlotValuesPicker
          v-else-if="tab === 'slot'"
          :slots="allSlots"
          @pick="onPick"
        />
        <ApiResponseTree
          v-else-if="tab === 'api'"
          :data="apiSample"
          @pick="onPick"
        />
      </div>
    </div>
  </div>
</template>
